import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import {catchError, filter, map, Observable, of, take, tap} from "rxjs";
import {GlobalStore} from "../global.store";
import {UsersService} from "../_services/users.service";
import {switchMap} from "rxjs/operators";
import {User} from "../_models/user";
import {AccessPolicyService} from "../_services/access-policy.service";

interface UserWithFlag {
  user: User | null;
  fromStore: boolean;
}

@Injectable({providedIn: 'root'})
export class AuthGuard  {
  constructor(
    private router: Router,
    private userService: UsersService,
    private globalStore: GlobalStore,
    private accessPolicyService: AccessPolicyService) {
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.globalStore.currentUser$.pipe(
      take(1),
      switchMap(userInStore => {
        if (userInStore) {
          return of<UserWithFlag>({ user: userInStore, fromStore: true });
        }
        return this.userService.getCurrentUser({skipCompanyScope: state.url.startsWith('/welcome')}).pipe(
          map(user => ({ user, fromStore: false }))
        );
      }),
      tap(({ user, fromStore }) => {
        if (!fromStore && user) {
          this.globalStore.updateCurrentUser(user);
        }
      }),
      switchMap(({ user }) => {
        if (!user) {
          return of(user);
        }

        if (!this.accessPolicyService.shouldLoadForUrl(state.url)) {
          return of(user);
        }

        return this.accessPolicyService.ensureLoadedForUrl(state.url).pipe(
          map(() => user),
          catchError(() => of(user))
        );
      }),
      map((user) => {
        if (user) {
          return true;
        }
        return this.router.createUrlTree(['/login'], {
          queryParams: { returnUrl: state.url }
        });
      })
    );
  }
}
